import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, ImagePlus } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import api, { formatApiErrorDetail } from '../../lib/api';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const GalleryAdmin = () => {
  const [photos, setPhotos] = useState([]);
  const [caption, setCaption] = useState('');
  const [takenAt, setTakenAt] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get('/gallery').then((res) => setPhotos(res.data || [])).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error('Please choose a photo to upload');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('caption', caption);
      form.append('taken_at', takenAt);
      await api.post('/admin/gallery', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Photo added to the gallery!');
      setCaption('');
      setTakenAt('');
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photo) => {
    if (!window.confirm('Remove this photo from the gallery?')) return;
    try {
      await api.delete(`/admin/gallery/${photo.id}`);
      toast.success('Photo removed');
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="admin-gallery-panel">
      <h2 className="font-serif text-xl text-[#3c4a2a] mb-6">Photo Gallery</h2>
      <Card className="border border-[#5d6b3c]/20 mb-8">
        <CardContent className="p-6">
          <form onSubmit={handleUpload} className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label htmlFor="photo-file" className="text-[#3c4a2a]">Photo (JPEG/PNG/WEBP, max 10 MB) *</Label>
              <Input
                id="photo-file"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                ref={fileRef}
                data-testid="gallery-file-input"
                className="mt-2 border-[#bd8cae]/50"
              />
            </div>
            <div>
              <Label htmlFor="photo-caption" className="text-[#3c4a2a]">Caption</Label>
              <Input
                id="photo-caption"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                data-testid="gallery-caption-input"
                className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
                placeholder="e.g., September Membership Meeting"
              />
            </div>
            <div>
              <Label htmlFor="photo-date" className="text-[#3c4a2a]">Date</Label>
              <Input
                id="photo-date"
                type="date"
                value={takenAt}
                onChange={(e) => setTakenAt(e.target.value)}
                data-testid="gallery-date-input"
                className="mt-2 border-[#bd8cae]/50 focus:border-[#5d6b3c]"
              />
            </div>
            <div className="sm:col-span-2">
              <Button
                type="submit"
                disabled={uploading}
                data-testid="gallery-upload-btn"
                className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? 'Uploading…' : 'Upload Photo'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {photos.length === 0 ? (
        <div className="text-center py-10 text-[#5f6d51]/60">
          <ImagePlus className="w-10 h-10 mx-auto mb-3 text-[#bd8cae]" />
          <p className="text-sm">No photos yet — upload the first one from your kickoff meeting!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((photo) => (
            <div key={photo.id} className="relative group rounded-lg overflow-hidden border border-[#5f6d51]/10" data-testid={`admin-gallery-photo-${photo.id}`}>
              <img
                src={`${API}/api/gallery/file/${photo.id}`}
                alt={photo.caption || 'Gallery photo'}
                className="w-full aspect-square object-cover"
              />
              <div className="p-2">
                <p className="text-xs text-[#3c4a2a] truncate">{photo.caption || '—'}</p>
                <p className="text-[10px] text-[#5f6d51]/60">{photo.taken_at}</p>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={() => handleDelete(photo)}
                data-testid={`admin-gallery-delete-${photo.id}`}
                className="absolute top-2 right-2 bg-white/90 border-[#d84f5b]/40 text-[#d84f5b] opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
